import { apiPath } from '@helpers/pathHelper';
import { FetchBaseQueryArgs } from '@reduxjs/toolkit/query';
import {
  BaseQueryFn,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchArgs,
} from '@reduxjs/toolkit/query';
import { enqueueSnackbar } from 'notistack';
import { z } from 'zod';
import qs from 'qs';
import { snakeCase } from 'lodash';
import { getImpersonationAccessToken } from '@features/auth/impersonation';

export const fetchBaseQueryWithAuth: (
  params: FetchBaseQueryArgs
) => BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =
  params => async (args, api, extraOptions) => {
    const baseQuery = fetchBaseQuery({
      ...params,
      baseUrl: apiPath(params.baseUrl ?? ''),
      credentials: 'include',
      prepareHeaders: headers => {
        const token = getImpersonationAccessToken();
        if (token) {
          headers.set('impersonation-authorization', token);
        }
        return headers;
      },
    });
    const result = await baseQuery(args, api, extraOptions);
    if (result.error?.status === 401) {
      handleUnauthorized();
    }
    return result;
  };

const handleUnauthorized = () => {
  enqueueSnackbar(
    'You are not authorized to make this request. Ensure you are logged in and have the correct permissions.',
    {
      variant: 'info',
      preventDuplicate: true,
    }
  );
};

export const tryZodParse = <T>(zodSchema: z.Schema<T>, value: unknown) => {
  try {
    return zodSchema.parse(value);
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const convertParamsToSnakeCase = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]) => [snakeCase(key), value])
  );

export const convertToRailsQueryString = (params: Record<string, unknown>) => {
  const snakeCaseParams = convertParamsToSnakeCase(params);

  return qs.stringify(snakeCaseParams, { arrayFormat: 'brackets' });
};

export const handleApiFailure = (error: unknown, message: string) => {
  console.error(error);
  enqueueSnackbar(message, {
    variant: 'error',
  });
};
